import React, { useState, useCallback } from 'react';
import { Search, TrendingUp, Download, AlertCircle, CheckCircle } from 'lucide-react';

const LOJAS = ['ANV', 'BVV', 'FER', 'GTV', 'LIN', 'MAL', 'MOX', 'PCI', 'PCS', 'RDP', 'SED', 'SVV', 'VEL', 'VSH'];

const formatarMoeda = (valor) => {
    const num = parseFloat(valor) || 0;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const inputStyle = {
    padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0',
    fontSize: '14px', color: '#334155', backgroundColor: '#fff',
    outline: 'none', width: '100%', boxSizing: 'border-box',
};

const labelStyle = {
    display: 'block', fontSize: '12px', fontWeight: '800', color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
};

const TOLERANCIA = 0.01;

// Verde = equilibrado | Azul = positivo (Fita > ATOS) | Vermelho = negativo (Fita < ATOS)
const corDiferenca = (diferenca) => {
    if (Math.abs(diferenca) <= TOLERANCIA) return { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' }; // verde
    if (diferenca > TOLERANCIA)            return { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' }; // azul
    return                                        { bg: '#fff1f2', text: '#e11d48', border: '#fecdd3' }; // vermelho
};

// Formata diferença com sinal explícito: +R$ 100,00 / -R$ 100,00 / R$ 0,00
const formatarDiferenca = (valor) => {
    const num = parseFloat(valor) || 0;
    const abs = Math.abs(num).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    if (num > TOLERANCIA)  return `+${abs}`;
    if (num < -TOLERANCIA) return `-${abs}`;
    return abs;
};

const FinanceiroFitaConciliacao = () => {
    const hoje  = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    const dataDefault = ontem.toISOString().split('T')[0];

    const [data,       setData]      = useState(dataDefault);
    const [loja,       setLoja]      = useState('');
    const [resultado,  setResultado] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro,       setErro]      = useState('');

    const token = localStorage.getItem('authToken');

    const consultar = useCallback(async () => {
        if (!data || !loja) { setErro('Selecione uma data e uma loja para consultar.'); return; }
        setCarregando(true); setErro(''); setResultado(null);
        try {
            const res = await fetch(
                `/api/fita/conciliacao?data=${data}&loja=${encodeURIComponent(loja)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Erro ${res.status}`); }
            setResultado(await res.json());
        } catch (e) {
            setErro(e.message || 'Erro ao consultar dados.');
        } finally {
            setCarregando(false);
        }
    }, [data, loja, token]);

    const exportarCSV = useCallback(() => {
        if (!resultado || resultado.registros.length === 0) return;

        // Valores consolidados (todos já vêm do endpoint /conciliacao):
        const vendaSovos      = resultado.venda_sovos ?? 0;     // Venda fiscal (SOVOS)
        const vendaFita       = resultado.totais.valor_flex;    // Fita = pagamentos registrados
        const recebimentoAtos = resultado.totais.valor_atos;    // ATOS = adquirente
        const difAtosFita     = resultado.totais.diferenca;     // Recebimento (ATOS) − Fita
        const difFitaSovos    = vendaFita - vendaSovos;         // mantém o cálculo já usado hoje

        const fmt = (v) => (v || 0).toFixed(2).replace('.', ',');

        const linhas = [
            ['Categoria', 'ATOS (R$)','Fita (R$)', 'Diferença (R$)'],
            ...resultado.registros.map(r => [
                r.categoria,
                fmt(r.valor_atos),
                fmt(r.valor_flex),
                fmt(r.diferenca),
            ]),
            ['TOTAL', fmt(recebimentoAtos), fmt(vendaFita), fmt(difAtosFita)],

            // Bloco resumo (2ª coluna = valor; demais ficam vazias)
            [],
            ['RESUMO', '', '', ''],
            ['Venda SOVOS',                          fmt(vendaSovos),      '', ''],
            ['Venda Fita',                           fmt(vendaFita),       '', ''],
            ['Recebimento ATOS',                     fmt(recebimentoAtos), '', ''],
            ['Diferença Recebimento (ATOS) x Fita',  fmt(difAtosFita),     '', ''],
            ['Diferença Venda (SOVOS) x Fita',       fmt(difFitaSovos),    '', ''],
        ];

        const csvContent = '﻿' + linhas
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
            .join('\r\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = `conciliacao_${resultado.loja}_${resultado.data}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [resultado]);

    const totalDivergente = resultado
        ? resultado.registros.filter(r => Math.abs(r.diferenca) > TOLERANCIA).length
        : 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

            <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                    Conciliação — Resumo de Venda
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>
                    Comparativo entre Fita (pagamentos registrados) e ATOS (adquirente), agrupados por categoria de pagamento.
                </p>
            </div>

            {/* FILTROS */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '16px', alignItems: 'flex-end' }}>
                    <div>
                        <label style={labelStyle}>Data</label>
                        <input type="date" value={data} onChange={e => setData(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Loja</label>
                        <select value={loja} onChange={e => setLoja(e.target.value)} style={inputStyle}>
                            <option value="">Selecione a loja...</option>
                            {LOJAS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </div>
                    <button
                        onClick={consultar}
                        disabled={carregando || !data || !loja}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '10px 24px', borderRadius: '8px', border: 'none', height: '42px',
                            whiteSpace: 'nowrap', fontWeight: '800', fontSize: '14px',
                            cursor: (!data || !loja || carregando) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: (!data || !loja || carregando) ? '#e2e8f0' : '#16a39c',
                            color: (!data || !loja || carregando) ? '#94a3b8' : '#fff',
                        }}
                    >
                        <Search size={16} />
                        {carregando ? 'Consultando...' : 'Consultar'}
                    </button>
                </div>
            </div>

            {/* ERRO */}
            {erro && (
                <div style={{ padding: '14px 18px', borderRadius: '10px', backgroundColor: '#fff1f2', border: '1px solid #fecdd3', color: '#e11d48', fontWeight: '600', fontSize: '14px' }}>
                    {erro}
                </div>
            )}

            {/* RESULTADO */}
            {resultado && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* CABEÇALHO */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                            Loja <strong>{resultado.loja}</strong> · {resultado.data.split('-').reverse().join('/')}
                        </span>
                        <button
                            onClick={exportarCSV}
                            disabled={resultado.registros.length === 0}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 18px', borderRadius: '8px', border: '1px solid #16a39c', backgroundColor: '#f0fdfa', color: '#16a39c', fontWeight: '700', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s' }}
                        >
                            <Download size={15} /> Exportar CSV
                        </button>
                    </div>

                    {/* RESUMO SOVOS x ATOS */}
                    {(() => {
                        const recebimentoAtos = resultado.totais.valor_atos;
                        const vendaSovos      = resultado.venda_sovos ?? 0;
                        const difSovosAtos    = recebimentoAtos - vendaSovos;
                        const coresDif        = corDiferenca(difSovosAtos);
                        return (
                            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <p style={{ margin: 0, fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        Diferença de Recebimento × Venda
                                    </p>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                                    {[
                                        { label: 'Recebimento ATOS', valor: recebimentoAtos, cor: '#0f172a', bg: '#fff', border: 'transparent' },
                                        { label: 'Venda SOVOS',      valor: vendaSovos,      cor: '#0f172a', bg: '#fff', border: 'transparent' },
                                        { label: 'Diferença',        valor: difSovosAtos,    cor: coresDif.text, bg: coresDif.bg, border: coresDif.border, sinal: true },
                                    ].map((item, i, arr) => (
                                        <div key={item.label} style={{
                                            padding: '18px 24px',
                                            backgroundColor: item.bg,
                                            borderRight: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none',
                                            borderTop: `3px solid ${item.border}`,
                                        }}>
                                            <p style={{ margin: '0 0 4px 0', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                {item.label}
                                            </p>
                                            <p style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: item.cor, fontVariantNumeric: 'tabular-nums' }}>
                                                {item.sinal ? formatarDiferenca(item.valor) : formatarMoeda(item.valor)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* CARDS RESUMO */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                        {[
                            { label: 'Total ATOS', valor: formatarMoeda(resultado.totais.valor_atos),  bg: '#eff6ff', cor: '#3b82f6' },
                            { label: 'Total Fita', valor: formatarMoeda(resultado.totais.valor_flex), bg: '#f0fdfa', cor: '#16a39c' },
                            {
                                label: 'Diferença Total',
                                valor: formatarDiferenca(resultado.totais.diferenca),
                                bg:  corDiferenca(resultado.totais.diferenca).bg,
                                cor: corDiferenca(resultado.totais.diferenca).text,
                            },
                            {
                                label: 'Categorias com Divergência',
                                valor: `${totalDivergente} / ${resultado.registros.length}`,
                                bg:  totalDivergente === 0 ? '#f0fdf4' : '#fff1f2',
                                cor: totalDivergente === 0 ? '#16a34a' : '#e11d48',
                                icon: totalDivergente === 0 ? <CheckCircle size={22} /> : <AlertCircle size={22} />,
                            },
                        ].map(c => (
                            <div key={c.label} style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ padding: '10px', borderRadius: '10px', backgroundColor: c.bg, color: c.cor }}>
                                    {c.icon ?? <TrendingUp size={22} />}
                                </div>
                                <div>
                                    <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>{c.label}</p>
                                    <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{c.valor}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* TABELA */}
                    {resultado.registros.length === 0 ? (
                        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '48px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>
                            Nenhum registro encontrado.
                        </div>
                    ) : (
                        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                            {[
                                                { label: 'Categoria',      align: 'left'  },
                                                { label: 'ATOS (R$)',      align: 'right' },
                                                { label: 'Fita (R$)',      align: 'right' },
                                                { label: 'Diferença (R$)', align: 'right' },
                                            ].map(h => (
                                                <th key={h.label} style={{ padding: '12px 20px', textAlign: h.align, fontWeight: '800', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    {h.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resultado.registros.map((row, idx) => {
                                            const cores = corDiferenca(row.diferenca);
                                            return (
                                                <tr key={row.categoria} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                    <td style={{ padding: '12px 20px', color: '#0f172a', fontWeight: '600' }}>{row.categoria}</td>
                                                    <td style={{ padding: '12px 20px', textAlign: 'right', color: '#334155', fontWeight: '500', fontVariantNumeric: 'tabular-nums' }}>
                                                        {formatarMoeda(row.valor_atos)}
                                                    </td>
                                                    <td style={{ padding: '12px 20px', textAlign: 'right', color: '#334155', fontWeight: '500', fontVariantNumeric: 'tabular-nums' }}>
                                                        {formatarMoeda(row.valor_flex)}
                                                    </td>
                                                    <td style={{ padding: '12px 20px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '700', backgroundColor: cores.bg, color: cores.text, border: `1px solid ${cores.border}` }}>
                                                            {formatarDiferenca(row.diferenca)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ backgroundColor: '#f0fdfa', borderTop: '2px solid #0d7a74' }}>
                                            <td style={{ padding: '14px 20px', fontWeight: '800', color: '#0d7a74' }}>TOTAL</td>
                                            <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '800', color: '#0d7a74', fontVariantNumeric: 'tabular-nums' }}>
                                                {formatarMoeda(resultado.totais.valor_atos)}
                                            </td>
                                            <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '800', color: '#0d7a74', fontVariantNumeric: 'tabular-nums' }}>
                                                {formatarMoeda(resultado.totais.valor_flex)}
                                            </td>
                                            <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                                                {(() => {
                                                    const cores = corDiferenca(resultado.totais.diferenca);
                                                    return (
                                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '800', backgroundColor: cores.bg, color: cores.text, border: `1px solid ${cores.border}` }}>
                                                            {formatarDiferenca(resultado.totais.diferenca)}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                        <tr style={{ backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                                            <td colSpan={2} style={{ padding: '12px 20px', fontWeight: '700', color: '#475569', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Diferença SOVOS / Fita
                                            </td>
                                            <td colSpan={2} style={{ padding: '12px 20px', textAlign: 'right' }}>
                                                {(() => {
                                                    const dif   = resultado.totais.valor_flex - (resultado.venda_sovos ?? 0) ;
                                                    const cores = corDiferenca(dif);
                                                    return (
                                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '800', backgroundColor: cores.bg, color: cores.text, border: `1px solid ${cores.border}` }}>
                                                            {formatarDiferenca(dif)}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            <div style={{ padding: '10px 20px', borderTop: '1px solid #f1f5f9', backgroundColor: '#f8fafc', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '3px', backgroundColor: '#bbf7d0' }}></span>
                                    OK (≤ R$ 0,01)
                                </span>
                                <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '3px', backgroundColor: '#bfdbfe' }}></span>
                                    Fita {'>'} ATOS
                                </span>
                                <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '3px', backgroundColor: '#fecdd3' }}></span>
                                    Fita {'<'} ATOS
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FinanceiroFitaConciliacao;
